import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Space, Typography, Alert, Spin, theme, message as antMessage } from 'antd';
import { MailOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { PiSparkleFill } from 'react-icons/pi';

import SupportService from 'services/SupportService';
import { varexeAPIResponseHandler } from 'services/api';

import TopicAction from './TopicAction';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

/*
| The support assistant.
|
| It answers from the support corpus in the API — the same topics this page lists below —
| and from nothing else. It cannot see invoices, customers, payments or any other
| organization data, which is the deliberate scope of the first version: a wrong answer
| about a real number costs far more than a missing one.
|
| Three things it does that a search box cannot:
|
|   1. Understands the symptom rather than the wording. "My logo isn't on the PDF" reaches
|      the branding topic without the person guessing our heading.
|   2. Carries the answer's sources with it, rendered as the same permission-gated buttons
|      the topic list uses, so an answer ends on the screen that fixes it.
|   3. Knows when it has failed, and offers a person instead of a longer paragraph.
|
| That last one is the point of `escalate`. An assistant that always produces something
| plausible is worse than no assistant, because the person keeps reading instead of asking.
*/

const SUGGESTIONS = [
    'My logo is missing from the invoice PDF',
    'How do I let customers pay online?',
    'A colleague cannot see the invoices page',
];

/**
 * @param {boolean} fill  Stretch to the height of the container instead of capping the
 *                        transcript. Set when the chat is the whole surface — the header
 *                        drawer — rather than one card among several on the support page.
 */
const AiSupportChat = ({ fill = false }) => {
    const { token } = theme.useToken();

    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    const [conversationId, setConversationId] = useState(null);
    const [escalated, setEscalated] = useState(false);

    const scrollRef = useRef(null);

    // Keep the newest turn in view. A reply that lands below the fold reads as nothing
    // having happened.
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, sending]);

    const send = async (text) => {
        const question = (text ?? draft).trim();
        if (!question || sending) return;

        setMessages((prev) => [...prev, { role: 'user', content: question }]);
        setDraft('');
        setSending(true);

        try {
            const response = await SupportService.ask(question, conversationId);
            const payload = response?.data?.data || {};

            setConversationId(payload.conversation_public_id || conversationId);
            setMessages((prev) => [
                ...prev,
                {
                    role: 'assistant',
                    content: payload.answer,
                    citedTopics: payload.cited_topics || [],
                    escalate: !!payload.escalate,
                    degraded: !!payload?.extra?.degraded,
                },
            ]);
        } catch (error) {
            /*
              The question stays on screen rather than being rolled back with the failure.
              Retyping it is the most irritating possible response to our outage, and the
              API has recorded it either way.
            */
            setMessages((prev) => [
                ...prev,
                {
                    role: 'assistant',
                    content:
                        'I could not reach the assistant just then. Try again, or send the question to support and a person will pick it up.',
                    citedTopics: [],
                    escalate: true,
                    failed: true,
                },
            ]);
            varexeAPIResponseHandler.handleError(error, { skipAutoHandling: true });
        } finally {
            setSending(false);
        }
    };

    const escalate = async () => {
        if (!conversationId || escalated) return;

        try {
            const response = await SupportService.escalate(conversationId);
            setEscalated(true);
            antMessage.success(response?.data?.message || 'Sent to support.');
        } catch (error) {
            varexeAPIResponseHandler.handleError(error, { skipAutoHandling: false });
        }
    };

    const empty = messages.length === 0;

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: fill ? 0 : 320,
                height: fill ? '100%' : undefined,
            }}
        >
            {/* ── Transcript ──────────────────────────────────────────────── */}
            <div
                ref={scrollRef}
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    // Capped on the page so a long chat cannot push the topic list off screen;
                    // uncapped in the drawer, where the chat IS the surface.
                    maxHeight: fill ? undefined : 420,
                    paddingInlineEnd: token.paddingXS,
                }}
            >
                {empty ? (
                    <Space orientation="vertical" size={12} style={{ display: 'flex' }}>
                        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                            Describe what you were doing and what happened instead. The assistant
                            answers from the help topics below — it cannot see your invoices,
                            customers or payments.
                        </Paragraph>

                        <Space size={[8, 8]} wrap>
                            {SUGGESTIONS.map((suggestion) => (
                                <Button
                                    key={suggestion}
                                    size="small"
                                    onClick={() => send(suggestion)}
                                    disabled={sending}
                                >
                                    {suggestion}
                                </Button>
                            ))}
                        </Space>
                    </Space>
                ) : (
                    <Space orientation="vertical" size={16} style={{ display: 'flex' }}>
                        {messages.map((entry, index) => (
                            <div key={index}>
                                {entry.role === 'user' ? (
                                    /*
                                      The person's own words, right-aligned in a tinted bubble. The
                                      asymmetry is the only thing distinguishing the two speakers at
                                      a glance, so it does the work a name label otherwise would.
                                    */
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <div
                                            style={{
                                                maxWidth: '85%',
                                                padding: `${token.paddingXS}px ${token.paddingSM}px`,
                                                background: token.colorFillSecondary,
                                                borderRadius: token.borderRadiusLG,
                                                color: token.colorText,
                                                whiteSpace: 'pre-wrap',
                                            }}
                                        >
                                            {entry.content}
                                        </div>
                                    </div>
                                ) : (
                                    <Space orientation="vertical" size={8} style={{ display: 'flex' }}>
                                        <Space align="start" size={8}>
                                            <span
                                                aria-hidden
                                                style={{
                                                    flex: 'none',
                                                    display: 'grid',
                                                    placeItems: 'center',
                                                    width: 24,
                                                    height: 24,
                                                    marginTop: 2,
                                                    fontSize: 13,
                                                    borderRadius: token.borderRadius,
                                                    color: entry.failed || entry.degraded
                                                        ? token.colorWarning
                                                        : token.colorPrimary,
                                                    background: entry.failed || entry.degraded
                                                        ? token.colorWarningBg
                                                        : token.colorPrimaryBg,
                                                }}
                                            >
                                                <PiSparkleFill />
                                            </span>
                                            <Text style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                                                {entry.content}
                                            </Text>
                                        </Space>

                                        {/*
                                          Where the answer came from. Shown as the topic's own
                                          heading and its action button, so the answer ends on the
                                          screen that fixes the problem rather than describing it —
                                          and so anyone can check the source rather than trusting
                                          the paraphrase.
                                        */}
                                        {entry.citedTopics?.length > 0 && (
                                            <div
                                                style={{
                                                    marginInlineStart: token.marginLG,
                                                    paddingInlineStart: token.paddingSM,
                                                    borderInlineStart: `2px solid ${token.colorBorderSecondary}`,
                                                }}
                                            >
                                                <Text
                                                    type="secondary"
                                                    style={{ fontSize: token.fontSizeSM, display: 'block' }}
                                                >
                                                    Based on
                                                </Text>
                                                {entry.citedTopics.map((topic) => (
                                                    <div key={topic.id} style={{ marginTop: token.marginXXS }}>
                                                        <Text style={{ fontSize: token.fontSizeSM }}>
                                                            {topic.question}
                                                        </Text>
                                                        <div>
                                                            <TopicAction action={topic.action} />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/*
                                          The assistant saying it could not help. Offered once, and
                                          only when it says so itself — an escalation button on every
                                          answer would read as a lack of confidence in all of them.
                                        */}
                                        {entry.escalate && index === messages.length - 1 && (
                                            <div style={{ marginInlineStart: token.marginLG }}>
                                                {escalated ? (
                                                    <Alert
                                                        type="success"
                                                        showIcon
                                                        message="Sent to support"
                                                        description="Someone will reply to the email address on your account. The conversation above goes with it, so you will not have to explain it twice."
                                                    />
                                                ) : (
                                                    <Button
                                                        icon={<MailOutlined />}
                                                        onClick={escalate}
                                                        disabled={!conversationId}
                                                    >
                                                        Send this to support
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </Space>
                                )}
                            </div>
                        ))}

                        {sending && (
                            <Space size={8}>
                                <Spin size="small" />
                                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                                    Reading the help topics…
                                </Text>
                            </Space>
                        )}
                    </Space>
                )}
            </div>

            {/* ── Composer ────────────────────────────────────────────────── */}
            <div style={{ marginTop: token.margin }}>
                <Space.Compact style={{ width: '100%' }}>
                    <TextArea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        placeholder="Ask a question…"
                        autoSize={{ minRows: 1, maxRows: 4 }}
                        disabled={sending}
                        /*
                          Enter sends, Shift+Enter breaks the line. The opposite is defensible in a
                          document editor; in a chat box it makes every message take two actions.
                        */
                        onPressEnter={(e) => {
                            if (!e.shiftKey) {
                                e.preventDefault();
                                send();
                            }
                        }}
                    />
                    <Button
                        type="primary"
                        icon={<ArrowUpOutlined />}
                        onClick={() => send()}
                        loading={sending}
                        disabled={!draft.trim()}
                    />
                </Space.Compact>

                <Text
                    type="secondary"
                    style={{ display: 'block', marginTop: token.marginXS, fontSize: token.fontSizeSM }}
                >
                    Answers come from the help topics below. Do not paste passwords or card
                    numbers — support will never ask for either.
                </Text>
            </div>
        </div>
    );
};

export default AiSupportChat;
